import ChatControl from './src/chatControl';
import rating from './src/rating';
import {
  ChatButtons as chatButtons,
  ChatMenu as chatMenu,
  TransferForm as transferForm,
} from './src/components';
import {
  TYPE_BUTTON_CLICK,
  TYPE_BUTTONS,
  TYPE_CONNECTING_STATUS,
  TYPE_CONTROL,
  TYPE_MENU,
  TYPE_NO_BUTTON_CLICK,
  TYPE_TRANSFER_DATA,
  TYPE_TRANSFER_FORM,
} from './src/customEventTypes';
import ConnectingStatus from './src/connectingStatus';
import predictionAnswer from './src/predictionAnswer';
import termsAndConditions from './src/termsAndConditions';

/* eslint-env browser */
(function (window, sf) {
  if (!sf?.liveAgentAPI) return;

  const EVENT_BUTTON_CLICK = 'sfButtonClick';
  const EVENT_FORM_SUBMIT = 'sfFormSubmit';
  const EVENT_SF_CUSTOMEVENT = 'sfCustomEvent';
  const STORAGE_LAST_CUSTOMEVENT = 'sfLastCustomEvent';
  const STORAGE_LAST_SESSIONID = 'sfLastSessionId';
  const COMPONENT_EVENTS = [TYPE_BUTTONS, TYPE_MENU, TYPE_TRANSFER_FORM];
  const CONTROL_EVENTS = [TYPE_CONTROL, TYPE_CONNECTING_STATUS];

  let sessionId;
  const { localStorage, document } = window;
  const chatControl = new ChatControl(localStorage);
  const connectingStatus = new ConnectingStatus(document, localStorage);
  const isTabPrimary = sf.liveAgentAPI?.browserSessionInfo?.isPrimary;
  const tabId = `${Date.now()}`;
  const bc = window.BroadcastChannel
    ? new window.BroadcastChannel('guuru_salesforce_liveagent')
    : undefined;

  const messageBroadcast = (eventKey, data) => {
    if (bc) {
      bc.postMessage({ eventKey, data });
    } else {
      // Fallback for browsers without BroadcastChannel
      const storageKey = `${eventKey}_${tabId}_${Date.now()}`;
      localStorage.setItem(storageKey, JSON.stringify(data));
      localStorage.removeItem(storageKey);
    }
  };

  const onMessageBroadcast = (eventKey, handler) => {
    if (bc) {
      bc.onmessage = (ev) => {
        if (ev.data.eventKey === eventKey) handler(eventKey, ev.data.data);
      };
    } else {
      // Fallback for browsers without BroadcastChannel
      window.addEventListener('storage', (ev) => {
        if (!ev
          || !ev.key.startsWith(`${eventKey}_`)
          || ev.key.startsWith(`${eventKey}_${tabId}_`)) {
          return;
        }

        handler(eventKey, JSON.parse(ev.newValue));
      });
    }
  };

  const setLastCustomEvent = (data = undefined) => {
    if (data) {
      localStorage.setItem(STORAGE_LAST_CUSTOMEVENT, JSON.stringify({
        sessionId, data,
      }));
    } else {
      localStorage.removeItem(STORAGE_LAST_CUSTOMEVENT);
    }
  };

  const getElementIndex = (el) => (
    el.previousElementSibling
      ? getElementIndex(el.previousElementSibling) + 1
      : 0
  );

  const hideInputBar = (hide = true) => {
    const inputWrapper = document.querySelector('div.chasitorInputWrapper');
    inputWrapper.style.display = hide ? 'none' : 'unset';
    if (!hide) {
      setTimeout(() => {
        const messageArea = document.querySelector('.embeddedServiceSidebar div.messageArea');
        messageArea.scrollTop = messageArea.scrollHeight;
      });
    }
  };

  const removeCustomElements = () => {
    const containers = document.querySelectorAll('.messageArea .guuruContainer');
    for (let i = 0; i < containers.length; i += 1) {
      containers[i].parentNode.removeChild(containers[i]);
    }
    if (containers.length) {
      setLastCustomEvent();
      hideInputBar(false);
    }
  };

  const sendTextMessage = (text) => {
    try {
      const textareaElement = document.querySelector('textarea.chasitorText');
      textareaElement.value = text;
      textareaElement.dispatchEvent(new window.KeyboardEvent('keydown', {
        key: 'Enter',
        code: 'Enter',
        charCode: 13,
        keyCode: 13,
        view: window,
        bubbles: true,
      }));
    } catch (err) {
      console.error('Failed to send button click message', err);
    }
  };

  const sendButtonClickCustomEvent = (data) => {
    sf.liveAgentAPI.sendCustomEvent(TYPE_BUTTON_CLICK, JSON.stringify(data));
  };

  const sendNoButtonClickCustomEvent = ({ type, data }) => {
    sf.liveAgentAPI.sendCustomEvent(TYPE_NO_BUTTON_CLICK, JSON.stringify({
      type,
      data,
    }));
  };

  const sendTransferFormCustomEvent = ({
    targetId, email, phone, attachments, customFields,
  }) => {
    sf.liveAgentAPI.sendCustomEvent(TYPE_TRANSFER_DATA, JSON.stringify({
      targetId, email, phone, attachments, customFields,
    }));
  };

  const onTransferFormSubmit = (data) => {
    sendTransferFormCustomEvent(data);
    messageBroadcast(EVENT_FORM_SUBMIT, {});
    removeCustomElements();
    setLastCustomEvent();
  };

  const onButtonClick = ({
    text, type, value, mute, data,
  }) => {
    if (type && value) {
      if (!mute) sendTextMessage(text);
      sendButtonClickCustomEvent({ type, value, data });
    } else {
      sendTextMessage(text);
    }
    messageBroadcast(EVENT_BUTTON_CLICK, {});
    removeCustomElements();
    setLastCustomEvent();
    hideInputBar(false);
  };

  const addElementToMessageArea = (element) => {
    const messageArea = document.querySelector('.embeddedServiceSidebar div.messageArea');
    const messageList = messageArea?.querySelector('ul');
    if (!messageList) return;

    element.setAttribute('data-element-index', messageList.children.length);
    messageList.appendChild(element);

    new MutationObserver((mutationsList, observer) => {
      const elementIndex = parseInt(element.dataset.elementIndex, 10);
      if (getElementIndex(element) === elementIndex) return;

      observer.disconnect();
      const markerElem = element.parentNode?.children[elementIndex - 1];
      if (markerElem) {
        markerElem.insertAdjacentElement('afterend', element);
      }
    })
      .observe(messageArea, {
        childList: true,
        subtree: true,
      });

    setTimeout(() => {
      messageArea.scrollTop = messageArea.scrollHeight;
    });
  };

  const handleCustomElementEvent = (result, isBroadcast) => {
    if (!isBroadcast) {
      setLastCustomEvent(result);
    }
    const { type, data } = result;
    try {
      removeCustomElements();
      if (!COMPONENT_EVENTS.includes(type)) return;

      const elData = JSON.parse(data);
      const clickHandler = onButtonClick;
      const confirmHandler = onTransferFormSubmit;
      const elements = {
        [TYPE_BUTTONS]: () => chatButtons({ ...elData, clickHandler }),
        [TYPE_MENU]: () => chatMenu({ ...elData, clickHandler }),
        [TYPE_TRANSFER_FORM]: () => transferForm({
          ...elData,
          confirmHandler,
          chatControl,
        }),
      };
      hideInputBar();
      addElementToMessageArea(elements[type]());
    } catch (err) {
      console.error('Failed to parse event data', data, err);
    }
  };

  const onCustomEvent = (eventType, isBroadcast = false) => (result) => {
    if (eventType !== result?.type) return;
    if (!isBroadcast) {
      messageBroadcast(EVENT_SF_CUSTOMEVENT, result);
    }
    switch (eventType) {
      case TYPE_CONTROL:
        chatControl.update(result, isBroadcast); break;
      case TYPE_CONNECTING_STATUS:
        connectingStatus.update(result, isBroadcast); break;
      default:
        handleCustomElementEvent(result, isBroadcast);
    }
  };

  const getLastCustomEvent = () => {
    const storedItem = localStorage.getItem(STORAGE_LAST_CUSTOMEVENT);
    const event = JSON.parse(storedItem);
    return sessionId && event?.sessionId === sessionId ? event.data : undefined;
  };

  const replayLastCustomEvent = () => {
    const lastCustomEvent = getLastCustomEvent();
    if (lastCustomEvent) {
      onCustomEvent(lastCustomEvent.type, true)(lastCustomEvent);
    }
  };

  let isMessageAreaVisible = true;
  const checkMessageAreaVisibility = () => {
    const sidebarBody = document.querySelector('.embeddedServiceSidebar .activeFeature');
    const observer = new MutationObserver(() => {
      const isMessageAreaVisibleNew = !!sidebarBody.querySelector('.embeddedServiceLiveAgentStateChat');
      if (!isMessageAreaVisible && isMessageAreaVisibleNew) {
        replayLastCustomEvent();
        connectingStatus.redraw();
      }
      isMessageAreaVisible = isMessageAreaVisibleNew;
    });
    observer.observe(sidebarBody, { childList: true, subtree: false });
  };

  const addCustomEventListeners = () => {
    if (!isTabPrimary) return;

    try {
      [...CONTROL_EVENTS, ...COMPONENT_EVENTS].forEach((evType) => (
        sf.liveAgentAPI.addCustomEventListener(evType, onCustomEvent(evType))
      ));
    } catch (err) {
      console.error('Failed to attach listeners');
    }
    checkMessageAreaVisibility();
  };

  const resetSession = (newSessionId) => {
    if (localStorage.getItem(STORAGE_LAST_SESSIONID) !== newSessionId) {
      localStorage.setItem(STORAGE_LAST_SESSIONID, newSessionId);

      chatControl.clear();
      connectingStatus.clear();
      setLastCustomEvent();
    }
  };

  const handleCustomEventsScriptsLoaded = ({ liveAgentSessionKey }) => {
    sessionId = liveAgentSessionKey;
    resetSession(sessionId);

    rating.patchSidebar(sf, chatControl);

    replayLastCustomEvent();
    connectingStatus.redraw();
    addCustomEventListeners();
    predictionAnswer.init(window, chatControl);
    termsAndConditions.init(window, chatControl);
  };

  const sendCustomEventAnswers = () => {
    const lastCustomEvent = getLastCustomEvent();
    if (!lastCustomEvent) return;

    switch (lastCustomEvent?.type) {
      case TYPE_TRANSFER_FORM:
      case TYPE_BUTTONS:
      case TYPE_MENU:
        sendNoButtonClickCustomEvent(lastCustomEvent); break;
      default: /* noop */
    }
    setLastCustomEvent();
  };

  const handleMessage = ({ data }) => {
    if (!isTabPrimary || data?.method !== 'liveagent.event') return;
    if (data.data.event !== 'chasitorChasitorChatMessage') return;

    sendCustomEventAnswers(data);
  };

  // Remove custom elements when messages are sent
  sf.addEventHandler('onChasitorMessage', () => removeCustomElements());
  // Remove custom elements the connecting status when messages are received
  sf.addEventHandler('onAgentMessage', () => {
    connectingStatus.clear();
    removeCustomElements();
  });
  // Clear control and connecting bar status when transferring to an agent
  sf.addEventHandler('onChatTransferInitiated', () => connectingStatus.clear());
  // Initialize the custom events
  sf.addEventHandler('onCustomEventsScriptsLoaded', handleCustomEventsScriptsLoaded);

  window.addEventListener('message', handleMessage);

  // Propagate custom events and button clicks to other tabs
  onMessageBroadcast(EVENT_FORM_SUBMIT, () => removeCustomElements());
  onMessageBroadcast(EVENT_BUTTON_CLICK, () => removeCustomElements());
  onMessageBroadcast(EVENT_SF_CUSTOMEVENT, (_, data) => (
    onCustomEvent(data.type, true)(data)
  ));

  chatControl.load();
  connectingStatus.load();

  if (typeof (window.Guuru) === 'undefined') {
    // eslint-disable-next-line no-param-reassign
    window.Guuru = () => ({
      getVersion: () => process.env.CI_COMMIT_SHORT_SHA,
    });
  }
}(window, window.embedded_svc));
